import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { FormField, FormItem, FormLabel, FormMessage } from '@nucleus-commerce/ui/components/form';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb, useMedia } from '@/features/media';
import { ProductStatusBadge, productQueryOptions, useProduct } from '@/features/products';
import { ProductPicker, type ProductPickerRow } from '@/features/resource-pickers';

import { MAX_RAIL_PRODUCTS, type RailFormInput } from '../../schemas/block-form-schema';

export interface RailProductsListProps {
  /** Null for a shared rail: publication then varies per store. */
  storeId: string | null;
  /**
   * Publication of the saved products in this store (`productId` → published), derived from
   * the row's `product_unpublished` warnings. Products missing from the map (just added) get no chip.
   */
  publication?: ReadonlyMap<string, boolean>;
}

/** Master product → its featured image. Two cached reads per row; the list is capped at 50. */
function ProductThumb({ productId }: { productId: string }) {
  const product = useProduct(productId);
  const media = useMedia(product.data?.featuredMediaId ?? null);
  return <MediaThumb media={media.data} className="size-10 shrink-0 border" />;
}

function PublicationChip({
  storeId,
  published,
}: {
  storeId: string | null;
  published: boolean | undefined;
}) {
  const { t } = useTranslation('content');
  if (storeId === null) {
    return (
      <Badge variant="outline" className="text-muted-foreground hidden sm:inline-flex">
        {t('rail.publication.varies')}
      </Badge>
    );
  }
  if (published === undefined) return null;
  return (
    <Badge
      variant="outline"
      className={cn(
        'hidden sm:inline-flex',
        published
          ? 'bg-success/15 text-success dark:bg-success/20 border-transparent'
          : 'bg-destructive/10 text-destructive border-transparent',
      )}
    >
      {published ? t('rail.publication.published') : t('rail.publication.notPublished')}
    </Badge>
  );
}

/** Ordered hand-picked products with thumbnail, handle, status and publication; up/down/remove per row. */
export function RailProductsList({ storeId, publication }: RailProductsListProps) {
  const { t } = useTranslation('content');
  const form = useFormContext<RailFormInput>();
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState(false);
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'products',
  });
  // The picker's cap is what is left of the rail's 50, so a rail never goes past it.
  const room = MAX_RAIL_PRODUCTS - fields.length;
  const chosen = new Set(fields.map((row) => row.productId));

  /** The rows to append, in picking order; pasted ids that never loaded are read. */
  const railRows = async (ids: string[], items: Array<ProductPickerRow<{ kind: 'org' }>>) => {
    const loaded = new Map(items.map((item) => [item.id, item] as const));
    const rows = await Promise.all(
      ids
        .filter((id) => !chosen.has(id))
        .map(async (id) => loaded.get(id) ?? queryClient.query(productQueryOptions(id))),
    );
    return rows.map((row) => ({
      productId: row.id,
      title: row.title,
      handle: row.handle,
      status: row.status,
    }));
  };

  return (
    <FormField
      control={form.control}
      name="products"
      render={() => (
        <FormItem>
          <div className="flex items-center justify-between gap-2">
            <FormLabel>{t('rail.products')}</FormLabel>
            {room > 0 ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setPicking(true)}>
                <Plus aria-hidden />
                {t('rail.addProducts')}
              </Button>
            ) : null}
          </div>
          {fields.length === 0 ? (
            <p className="text-muted-foreground rounded-md border border-dashed p-4 text-center text-sm">
              {t('rail.noProducts')}
            </p>
          ) : (
            <ol className="divide-y rounded-md border" aria-label={t('rail.products')}>
              {fields.map((row, index) => (
                <li key={row.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="text-muted-foreground w-5 shrink-0 text-right tabular-nums">
                    {index + 1}
                  </span>
                  <ProductThumb productId={row.productId} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{row.title}</span>
                    <span className="text-muted-foreground block truncate font-mono text-[11px]">
                      {row.handle}
                    </span>
                  </span>
                  <ProductStatusBadge status={row.status as 'draft' | 'active' | 'archived'} />
                  <PublicationChip storeId={storeId} published={publication?.get(row.productId)} />
                  <span className="flex shrink-0 items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={index === 0}
                      onClick={() => move(index, index - 1)}
                      aria-label={t('rail.moveUp', { title: row.title })}
                    >
                      <ArrowUp aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={index === fields.length - 1}
                      onClick={() => move(index, index + 1)}
                      aria-label={t('rail.moveDown', { title: row.title })}
                    >
                      <ArrowDown aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => remove(index)}
                      aria-label={t('rail.removeProduct', { title: row.title })}
                    >
                      <X aria-hidden />
                    </Button>
                  </span>
                </li>
              ))}
            </ol>
          )}
          <FormMessage />
          {room > 0 ? (
            <ProductPicker
              open={picking}
              onOpenChange={setPicking}
              scope={{ kind: 'org' }}
              {...(storeId ? { storeId } : {})}
              multiple={room}
              lockedIds={chosen}
              onConfirm={async (selection) => {
                const rows = await railRows(selection.ids, selection.items);
                for (const row of rows) append(row);
              }}
            />
          ) : null}
        </FormItem>
      )}
    />
  );
}
